/**
 * Lightweight DOM-based toast for permission errors.
 *
 * The Axios interceptor runs outside React context, so we cannot use
 * the React-based ToastProvider. This utility renders a self-dismissing
 * toast directly into the DOM, styled to match the existing toast system.
 */

const TOAST_CONTAINER_ID = 'permission-toast-container';
const AUTO_DISMISS_MS = 6_000;

interface PermissionToastOptions {
  message: string;
  severity?: 'error' | 'warning';
}

const SEVERITY_CONFIG = {
  error: {
    border: 'border-l-red-500',
    bg: 'bg-red-50',
    icon: 'block',
    iconColor: 'text-red-500',
  },
  warning: {
    border: 'border-l-amber-500',
    bg: 'bg-amber-50',
    icon: 'gpp_maybe',
    iconColor: 'text-amber-500',
  },
} as const;

function getOrCreateContainer(): HTMLElement {
  let container = document.getElementById(TOAST_CONTAINER_ID);
  if (!container) {
    container = document.createElement('div');
    container.id = TOAST_CONTAINER_ID;
    container.className = 'fixed top-4 right-4 z-[60] flex flex-col gap-3 w-96 pointer-events-none';
    document.body.appendChild(container);
  }
  return container;
}

/**
 * Show a permission-related toast notification.
 * Used by the Axios interceptor to display 403 error messages.
 */
export function showPermissionToast({ message, severity = 'error' }: PermissionToastOptions): void {
  const container = getOrCreateContainer();
  const config = SEVERITY_CONFIG[severity];

  const toast = document.createElement('div');
  toast.className = `pointer-events-auto rounded-lg shadow-lg p-4 border-l-4 ${config.border} ${config.bg} animate-slide-in`;
  toast.setAttribute('role', 'alert');
  toast.setAttribute('data-testid', 'permission-toast');

  toast.innerHTML = `
    <div class="flex items-start gap-3">
      <span class="material-symbols-outlined mt-0.5 ${config.iconColor}">${config.icon}</span>
      <div class="flex-1 min-w-0">
        <p class="text-sm font-semibold text-slate-900">Permission Denied</p>
        <p class="text-xs text-slate-600 mt-1">${escapeHtml(message)}</p>
      </div>
      <button
        type="button"
        class="text-slate-400 hover:text-slate-600 cursor-pointer"
        aria-label="Dismiss"
      >
        <span class="material-symbols-outlined text-base">close</span>
      </button>
    </div>
  `;

  // Dismiss on close button click
  const closeBtn = toast.querySelector('button');
  if (closeBtn) {
    closeBtn.addEventListener('click', () => removeToast(toast));
  }

  container.prepend(toast);

  // Auto-dismiss
  setTimeout(() => removeToast(toast), AUTO_DISMISS_MS);
}

function removeToast(toast: HTMLElement): void {
  toast.style.opacity = '0';
  toast.style.transition = 'opacity 200ms ease-out';
  setTimeout(() => toast.remove(), 200);
}

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}
